import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';

import { createCharacter, CreatedCharacter } from '../../../helpers/api/admin/character';

export const MAX_CHARACTER_AVATAR_SIZE = 5 * 1024 * 1024;
export const CHARACTER_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const validateCharacterAvatar = (file: File) => {
  if (!CHARACTER_AVATAR_TYPES.includes(file.type)) {
    return 'Choose a valid JPEG, PNG, or WebP image.';
  }
  if (file.size > MAX_CHARACTER_AVATAR_SIZE) {
    return 'The avatar must be 5 MB or smaller.';
  }
  return null;
};

interface CreateCharacterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (character: CreatedCharacter) => void;
}

const CreateCharacterModal = ({ isOpen, onClose, onCreated }: CreateCharacterModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<File | null>(null);
  const [nameError, setNameError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [requestError, setRequestError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setName('');
    setAvatar(null);
    setNameError('');
    setAvatarError('');
    setRequestError('');
    setIsDragging(false);
  }, [isOpen]);

  const selectAvatar = (file?: File) => {
    if (!file) return;
    const validationError = validateCharacterAvatar(file);
    if (validationError) {
      setAvatar(null);
      setAvatarError(validationError);
      return;
    }
    setAvatar(file);
    setAvatarError('');
    setRequestError('');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const nextNameError = !trimmedName
      ? 'Name is required.'
      : trimmedName.length > 255
        ? 'Name must contain 255 characters or fewer.'
        : '';
    const nextAvatarError = avatar ? validateCharacterAvatar(avatar) || '' : 'Avatar is required.';
    setNameError(nextNameError);
    setAvatarError(nextAvatarError);
    setRequestError('');
    if (nextNameError || nextAvatarError || !avatar) return;

    setSaving(true);
    try {
      const character = await createCharacter({ name: trimmedName, avatar });
      onCreated(character);
      onClose();
    } catch (caught) {
      setRequestError(caught instanceof Error ? caught.message : 'Unable to create the character. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered fade={false} className="character-create-modal">
        <form onSubmit={handleSubmit} noValidate>
          <ModalHeader toggle={saving ? undefined : onClose}>Create character</ModalHeader>
          <ModalBody>
            <div className="character-create-modal__constraints" role="note" data-cy="character-avatar-constraints">
              <i className="ri-information-line" aria-hidden="true" />
              <div>
                <strong>Avatar requirements</strong>
                <span>JPEG, PNG or WebP. Maximum file size: 5 MB.</span>
              </div>
            </div>

            {requestError && <div className="alert alert-danger" role="alert">{requestError}</div>}

            <div className="mb-3">
              <Label htmlFor="character-name">Name</Label>
              <Input
                id="character-name"
                value={name}
                maxLength={255}
                invalid={Boolean(nameError)}
                disabled={saving}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError('');
                  setRequestError('');
                }}
                aria-describedby={nameError ? 'character-name-error' : undefined}
                data-cy="character-name-input"
              />
              {nameError && <div id="character-name-error" className="invalid-feedback">{nameError}</div>}
            </div>

            <div>
              <Label>Avatar</Label>
              <div
                className={`character-create-modal__dropzone${isDragging ? ' is-dragging' : ''}${avatarError ? ' is-invalid' : ''}`}
                onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDragging(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  selectAvatar(event.dataTransfer.files[0]);
                }}
                data-cy="character-avatar-dropzone"
              >
                <i className="ri-image-add-line" aria-hidden="true" />
                <strong>{avatar ? avatar.name : 'Drag and drop an image here'}</strong>
                <span>{avatar ? 'Choose another image if needed' : 'or select a file from your device'}</span>
                <Button type="button" color="secondary" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                  Choose image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(event) => {
                    selectAvatar(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                  data-cy="character-avatar-input"
                />
              </div>
              {avatarError && <div className="character-create-modal__field-error" role="alert">{avatarError}</div>}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" color="primary" disabled={saving} data-cy="character-save">Save</Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={saving} centered fade={false} backdrop="static" keyboard={false} className="character-saving-modal">
        <ModalBody role="status" aria-live="polite" data-cy="character-saving-modal">
          <Spinner aria-hidden="true" />
          <div>
            <strong>Creating character</strong>
            <span>Please wait while the request completes.</span>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CreateCharacterModal;
